import * as React from 'react';
import Table from '@mui/material/Table';
import Pagination from '@mui/material/Pagination';
import {Stack} from '@mui/material';
import { useRef } from 'react';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CustomSwitch from '../customSwitch/CustomSwitch';
import { Button } from '@mui/material';
import { Typography } from '@mui/material';
import StatusBadge from '../statusBadge/StatusBadge';
import { getFailedReports, getViewButtonData, getViewButtonRecords } from '../../services/integrationService';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import {IconButton} from '@mui/material';
import { saveAs } from 'file-saver';
import Loader from '../loading/Loader';
import * as XLSX from 'xlsx'
import { TranslationEnum, usePortalTranslation } from '../../providers/TranslationProvider';

type ValidationRulesTableProps = {
  data: any[];
  onSwitchToggle: (rowData: any, isChecked: boolean) => void;
  selectedPage:string
};

const headers: string[] = ['ID', 'DESCRIPTION', 'STATUS', 'LAST UPDATED', 'ACTIVE CHECK'];

const ValidationsRulesTable: React.FC<ValidationRulesTableProps> = ({ data, onSwitchToggle, selectedPage}) => {
    const { translate } = usePortalTranslation();
    const CUR_COMPONENT = "ValidationDivision"
    const lob = localStorage.getItem('lob');
    const tableRef = useRef(null);
    const PAGE_SIZE = 30;
    const [fetchedData, setFetchedData] = React.useState<any[]>([]);
    const [pageSize, setPageSize] = React.useState<number>(10);
    const [totalPages, setTotalPages] = React.useState<number>(1);
    const [currentPage, setCurrentPage] = React.useState<number>(1);

  const [clickedRowName, setClickedRowName] = React.useState<string>('');
  const [clickedRowID, setClickedRowID] = React.useState<string>('');
 const [clickedRuleNumber, setClickedRuleNumber] = React.useState<number>();
  const [isTableVisible, setTableVisible] = React.useState<boolean>(false);
  const [isLoading, setLoading] = React.useState<boolean>(false);
  
  const handleViewButtonClick = async (row: any, ruleNumber: number) => {
    setTableVisible(true);
    setLoading(true);
  
    try {
      if (lob) {
        const numberOfRecords = await getViewButtonRecords(row.id, lob);
        
        if (!numberOfRecords || !numberOfRecords.data) {
          setFetchedData([]); 
          setLoading(false)
        } else {
          const totalRecords = numberOfRecords.data.size;
          const calculatedPageSize = Math.min(totalRecords, PAGE_SIZE);
          const calculatedTotalPages = Math.ceil(totalRecords / calculatedPageSize);
          setPageSize(calculatedPageSize);
          setTotalPages(calculatedTotalPages);
  
          const data = await getViewButtonData(row.id, lob, 1);
          if (data && data.data) {
            setFetchedData(data.data);
            setClickedRowName(row.message);
            setClickedRowID(row.id);
            setClickedRuleNumber(ruleNumber);
            setCurrentPage(1);
          } else {
            setFetchedData([]); 
          }
        }
      }
    } catch (error) {
      console.log("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePageChange = async (event:React.ChangeEvent<unknown>, newPage:number) => {
    setLoading(true); 
    setCurrentPage(newPage); 
  
    
    if (lob) {
      const data = await getViewButtonData(clickedRowID, lob, newPage);
      setFetchedData(data.data);
    }
  
    setLoading(false); 
  };
  
  const handleSwitchToggle = (row: any, isChecked: boolean) => {
    onSwitchToggle(row, isChecked);
    if ( isChecked && row.status.toLowerCase() === 'pass') {
    
      setTableVisible(false);
      setFetchedData([]); 
      setClickedRowName('');
      setClickedRowID("");
     
    }
    
  };
  
  const handleDownloadExcel = async() => {
    
  
try{
      const failedReports = await getFailedReports(lob||"", clickedRowID);
      const worksheet = XLSX.utils.json_to_sheet(failedReports.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Combined Data');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const now = new Date();
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = monthNames[now.getMonth()];
      const formattedDate = `${now.getDate()}_${monthName}_${now.getFullYear()}`;
      const formattedTime = `${now.getHours()}_${now.getMinutes()}_${now.getSeconds()}`;
      const filename = `${clickedRowID}_${formattedDate}_${formattedTime}_failed Records.xlsx`;

      const excelData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(excelData, filename);
     
    }
    catch(error){
      console.log("Error exporting the data:", error);
    }
  };
  const renderViewButton = (row: any, ruleNumber:number) => {
    if (row.status.toLowerCase() === 'fail') {
      return (
        <Button onClick={() => handleViewButtonClick(row,ruleNumber)} variant="contained" sx={{backgroundColor:"#cb0c9f",borderRadius:"7px", "&:hover": {
            backgroundColor: "#cb0c9f"}}}>
           {translate(TranslationEnum.validation_division,"View")}
        </Button>
      );
    }
    return null;
  };
  React.useEffect(() => {
    setTableVisible(false); 
    setFetchedData([]);
  }, [selectedPage]);
  
  



  return (
    <>
    
      <TableContainer
        component={Paper}
        sx={{
          width: '150%',
          paddingTop: '5rem',
          position: 'relative',
          fontWeight: 'bold',
          borderRadius: '10px',
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
         
        }}
      >
        <Typography variant="h6" fontWeight="medium" sx={{ position: 'absolute', left: '20px', top: '30px' }}>
        {translate(TranslationEnum.validation_division,"Rules")}
        </Typography>
        <Table sx={{ minWidth: 650, width: '100%' }} size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header} sx={{ color: 'grey', fontWeight: 'bold',whiteSpace:"nowrap" }} align={header === 'ACTIVE CHECK' ? 'left' : 'center'}>
                  {translate(TranslationEnum.validation_division,header)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }}}>
                <TableCell align="center" component="th" scope="row" sx={{whiteSpace:"nowrap"}}>
                  {`${translate(TranslationEnum.validation_division,"Rules")} ${index + 1}`}
                </TableCell>
                <TableCell align="center"> {translate(TranslationEnum.validation_division,row.description)}</TableCell>
                <TableCell align="center" sx={{ textTransform: 'uppercase' }}>
                  {' '}
                  <StatusBadge content={row.status} />
                </TableCell>
                <TableCell align="center">
                  {' '}
                  {new Date(row.executionTime).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </TableCell>
                <TableCell align="center" style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CustomSwitch isChecked={row.active === 'active'} onToggle={(isChecked) => handleSwitchToggle(row, isChecked)}  />
                  </div>
                </TableCell>
                <TableCell align="center">{renderViewButton(row, index+1)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isTableVisible && (
        <>
        {isLoading ? (<Loader/>):fetchedData.length===0? (<Typography variant="body1" sx={{ width:"70vw",textAlign: 'center', marginTop: '1rem' }}>
              No data to show
            </Typography>):(
          <TableContainer component={Paper} sx={{ marginTop: '2rem', width: "150%", position: "relative", overflowX: "auto" }}>
            <IconButton onClick={handleDownloadExcel} sx={{
              position: 'absolute',
              top: '0.2rem',
              left: '90%',
              transform: 'translateX(-50%)',
              zIndex: 1,
            }}>
              <SimCardDownloadIcon fontSize='medium' />
            </IconButton>

            <Typography
              sx={{ p: 3, fontWeight: "bold" }}>
              {`Rule no.  ${clickedRuleNumber} - `}
              {clickedRowName}</Typography>

            <Table ref={tableRef} sx={{ minWidth: 650 }} size="small" aria-label="a dense table">

              <TableHead>
                <TableRow>
                  {Object.keys(fetchedData[0]).map((key) => (
                    <TableCell key={key} sx={{ color: 'grey', fontWeight: 'bold' }} align="center">
                      {key.toUpperCase()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fetchedData.map((rowData, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Object.values(rowData).map((value: any, index) => (
                      <TableCell key={index} align="center">
                        {value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}


              </TableBody>
            </Table>
          </TableContainer>
            )}
            {!isLoading &&  fetchedData.length>0 && totalPages>1 &&
            <Stack sx={{width:"150%", alignItems:"center",marginTop:3}}>
            <Pagination count={totalPages}   page={currentPage} onChange={(event,newPage)=>handlePageChange(event,newPage)}
            sx={{
              '.MuiPaginationItem-root.Mui-selected': {
                backgroundColor: '#24c6b1',
              },
            }}
            
            />
            </Stack>
            }
        </>
      )}
      </>
  );
};

export default ValidationsRulesTable;
